import { useCallback } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

// Default query options (similar to React Query defaults)
const DEFAULT_QUERY_OPTIONS = {
  enabled: false,
  retry: 3,
  staleTime: 5 * 60 * 1000, // 5 minutes
};

const normalizeBaseUrl = (raw) => {
  if (!raw) return '';
  return String(raw).trim().replace(/\/+$/, '');
};

const ensureApiPrefix = (path) => {
  const p = String(path || '');
  if (!p.startsWith('/')) return `/api/${p}`;
  return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
};

const buildApiUrl = (endpoint) => {
  const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
  const path = ensureApiPrefix(endpoint);

  // Allow BASE_URL to be either:
  // - a full origin (e.g. "http://localhost:3001" or "http://localhost:3001/api")
  // - a proxy path (e.g. "/api")
  if (!base) return path;

  // If BASE_URL already ends with "/api", avoid doubling.
  const baseNoApi = base.endsWith('/api') ? base.slice(0, -4) : base;

  if (base.startsWith('/')) {
    // If someone sets BASE_URL to "/api", avoid doubling it.
    if (base === '/api' && path.startsWith('/api')) return path;
    return `${baseNoApi}${path}`;
  }
  return `${baseNoApi}${path}`;
};

// API request helper
const apiRequest = async (authenticatedFetch, endpoint) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestJson = async (authenticatedFetch, endpoint, body) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestJsonWithMethod = async (authenticatedFetch, endpoint, method, body) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, {
    method,
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestWithMethod = async (authenticatedFetch, endpoint, method) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, { method });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const invalidateTaskOrderSpendingQueries = async (queryClient) => {
  await queryClient.invalidateQueries({ queryKey: ['taskOrderTaskSpendingByMonth'] });
  await queryClient.invalidateQueries({ queryKey: ['taskOrderActualDollarsByTask'] });
};

const invalidateGanttQueries = async (queryClient) => {
  await queryClient.invalidateQueries({ queryKey: ['projectGantt'] });
  await queryClient.invalidateQueries({ queryKey: ['taskOrderGantt'] });
};

const invalidateUserRateRelatedQueries = async (queryClient, userId = null) => {
  if (Number.isInteger(userId)) {
    await queryClient.invalidateQueries({ queryKey: ['userRates', userId] });
    await queryClient.invalidateQueries({ queryKey: ['user', userId] });
  } else {
    await queryClient.invalidateQueries({ queryKey: ['userRates'] });
  }

  await queryClient.invalidateQueries({ queryKey: ['users'] });
  await queryClient.invalidateQueries({ queryKey: ['rates'] });

  // Rates affect forecast cost calculations across the app.
  await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
  await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
  await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
  await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
  await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
  await queryClient.invalidateQueries({ queryKey: ['taskHourForecastForMonths'] });
  await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
  await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
  await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });

  // Charts/summary views may incorporate rates.
  await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
  await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

  await invalidateTaskOrderSpendingQueries(queryClient);
};

const normalizeTaskOrderId = (rawTaskOrderId) => {
  const taskOrderId = Number(rawTaskOrderId);
  return Number.isInteger(taskOrderId) && taskOrderId > 0 ? taskOrderId : null;
};


// Test API query hook
export const useTestApi = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['testApi'],
    queryFn: () => apiRequest(authenticatedFetch, '/test'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useRuntimeFeatureFlags = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['runtimeFeatureFlags'],
    queryFn: () => apiRequest(authenticatedFetch, '/runtime-config/features'),
    enabled: optionsEnabled ?? true,
    staleTime: 5 * 60 * 1000,
    ...restOptions,
  });
};

export const useHasConnectSuite = (options = {}) => {
  const { data } = useRuntimeFeatureFlags(options);
  return data?.hasConnectSuite !== false;
};

// True only when the server has PURSUITCONNECT_API_URL set. Defaults to false
// so the UI never offers a pull that is guaranteed to fail.
export const useHasPursuitConnect = (options = {}) => {
  const { data } = useRuntimeFeatureFlags(options);
  return data?.hasPursuitConnect === true;
};

export const usePursuitConnectPursuits = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['pursuitConnectPursuits'],
    queryFn: async () => {
      // Errors carry the server's message (e.g. no access to pursuits in PursuitConnect).
      const json = await timesheetRequest(authenticatedFetch, '/connect/pursuitconnect/pursuits');
      if (Array.isArray(json)) return json;
      return Array.isArray(json?.data) ? json.data : [];
    },
    enabled: optionsEnabled ?? true,
    staleTime: 60_000,
    ...restOptions,
  });
};

export const useCreatePursuitConnectProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/connect/pursuitconnect/projects', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Project lists come back with every project, each saying whether this user can open it. Projects
// they aren't on aren't shown at all.
const onlyOpenable = (rows) => (Array.isArray(rows) ? rows.filter((p) => p?.canOpen !== false) : rows);

export const useProjects = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, includeArchived = false, ...restOptions } = options;

  const includeArchivedFlag = includeArchived === true;
  const endpoint = includeArchivedFlag ? '/projects?includeArchived=true' : '/projects';

  return useQuery({
    queryKey: ['projects', includeArchivedFlag ? 'with-archived' : 'active-only'],
    queryFn: () => apiRequest(authenticatedFetch, endpoint),
    select: onlyOpenable,
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useArchivedProjects = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['archivedProjects'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects/archived'),
    select: onlyOpenable,
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useTrashedProjects = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['trashedProjects'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects/trash'),
    select: onlyOpenable,
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useCreateProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/projects', payload),
    onSuccess: async (data, variables, context) => {
      // The creator becomes a member with every right, which /me reports.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUploadProjectPhoto = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/photo`, payload);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', pid] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteProjectPhoto = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId }) => {
      const pid = Number(projectId);
      return apiRequestWithMethod(authenticatedFetch, `/projects/${pid}/photo`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', pid] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTaskOrders = (projectIdOrOptions = {}, maybeOptions = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const projectId = Number.isInteger(projectIdOrOptions) ? projectIdOrOptions : null;
  const options = Number.isInteger(projectIdOrOptions) ? (maybeOptions || {}) : (projectIdOrOptions || {});
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrders', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders`),
    enabled: optionsEnabled ?? Number.isInteger(projectId),
    staleTime: 30_000,
    ...restOptions,
  });
};

export const useProjectMilestones = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);
  const qs = Number.isInteger(taskOrderId) ? `?task_order_id=${encodeURIComponent(String(taskOrderId))}` : '';

  return useQuery({
    queryKey: ['milestones', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/milestones${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/milestones`, payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, milestoneId }) => {
      const pid = Number(projectId);
      return apiRequestWithMethod(authenticatedFetch, `/projects/${pid}/milestones/${milestoneId}`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, milestoneId, payload }) => {
      const pid = Number(projectId);
      const mid = Number(milestoneId);
      return apiRequestJsonWithMethod(authenticatedFetch, `/projects/${pid}/milestones/${mid}`, 'PUT', payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useCreateTaskOrder = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/task-orders`, payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders', pid] });
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders'] });
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTaskOrder = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, payload }) => {
      const pid = Number(projectId);
      const toid = Number(taskOrderId);
      return apiRequestJsonWithMethod(authenticatedFetch, `/projects/${pid}/task-orders/${toid}`, 'PUT', payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders', pid] });
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders'] });
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTaskOrder = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId }) => {
      const pid = Number(projectId);
      const toid = Number(taskOrderId);
      return apiRequestWithMethod(authenticatedFetch, `/projects/${pid}/task-orders/${toid}`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders'] });
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOrderHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOrderActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTasks = (projectIdOrParamsOrOptions = {}, maybeOptions = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  // Back-compat: useTasks(projectId, options)
  // New: useTasks({ projectId, taskOrderId }, options)
  const params =
    Number.isInteger(projectIdOrParamsOrOptions) || projectIdOrParamsOrOptions === null
      ? { projectId: Number.isInteger(projectIdOrParamsOrOptions) ? projectIdOrParamsOrOptions : null, taskOrderId: null }
      : (projectIdOrParamsOrOptions || {});

  const projectId = Number.isInteger(params?.projectId) ? Number(params.projectId) : null;
  const taskOrderId = normalizeTaskOrderId(params?.taskOrderId);

  const options =
    Number.isInteger(projectIdOrParamsOrOptions) || projectIdOrParamsOrOptions === null
      ? (maybeOptions || {})
      : (maybeOptions && Object.keys(maybeOptions).length > 0 ? maybeOptions : params);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const projectPart = Number.isInteger(taskOrderId)
    ? `?task_order_id=${taskOrderId}`
    : Number.isInteger(projectId)
      ? `?project_id=${projectId}`
      : '';

  return useQuery({
    queryKey: ['tasks', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks${projectPart}`),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/tasks', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, payload }) => apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}`, 'PUT', payload),
    onMutate: async (variables) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);
      const nextTask = variables?.payload?.task;
      const nextBudgetTarget = variables?.payload?.budgetTarget ?? variables?.payload?.budget_target;

      const hasProjectId = Number.isInteger(projectId);
      const hasTaskId = Number.isInteger(taskId);
      const hasTaskName = typeof nextTask === 'string';
      const hasBudgetTarget = nextBudgetTarget !== undefined;

      const snapshots = {
        tasks: hasProjectId ? queryClient.getQueriesData({ queryKey: ['tasks', projectId] }) : undefined,
        task: hasTaskId ? queryClient.getQueryData(['task', taskId]) : undefined,
      };

      if (hasProjectId && hasTaskId && (hasTaskName || hasBudgetTarget)) {
        queryClient.setQueriesData({ queryKey: ['tasks', projectId] }, (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((t) => {
            if (Number(t?.id) !== taskId) return t;
            const patch = {};
            if (hasTaskName) patch.task = nextTask;
            if (hasBudgetTarget) patch.budgetTarget = nextBudgetTarget === '' ? null : nextBudgetTarget;
            return { ...t, ...patch };
          });
        });
      }
      if (hasTaskId && (hasTaskName || hasBudgetTarget)) {
        queryClient.setQueryData(['task', taskId], (old) => {
          if (!old) return old;
          const patch = {};
          if (hasTaskName) patch.task = nextTask;
          if (hasBudgetTarget) patch.budgetTarget = nextBudgetTarget === '' ? null : nextBudgetTarget;
          return { ...old, ...patch };
        });
      }

      return snapshots;
    },
    onError: async (_err, variables, context) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);

      if (Number.isInteger(projectId) && Array.isArray(context?.tasks)) {
        for (const [key, data] of context.tasks) {
          queryClient.setQueryData(key, data);
        }
      }
      if (Number.isInteger(taskId) && context?.task !== undefined) {
        queryClient.setQueryData(['task', taskId], context.task);
      }
    },
    onSuccess: async (data, variables, context) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
      if (Number.isInteger(taskId)) {
        await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      }

      await invalidateGanttQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useCreateActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, payload }) => apiRequestJson(authenticatedFetch, `/tasks/${taskId}/activities`, payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      // Hour edits affect project-level activity totals shown in the forecast dropdown.
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await invalidateGanttQueries(queryClient);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUsers = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiRequest(authenticatedFetch, '/users'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

// Who is signed in, what they may do, the permission catalog and the projects they are a member of.
export const useMe = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiRequest(authenticatedFetch, '/users/me'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useSetUserPermissions = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, permissions }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/users/${userId}/permissions`, 'PUT', { permissions }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertUser = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/users', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteUser = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (userId) => apiRequestWithMethod(authenticatedFetch, `/users/${userId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Deactivated users can't sign in; reactivating needs a free seat (409 otherwise).
export const useSetUserActive = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, active }) =>
      apiRequestJson(authenticatedFetch, `/users/${userId}/${active ? 'reactivate' : 'deactivate'}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    ...options,
  });
};

export const useInvites = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['invites'],
    queryFn: () => apiRequest(authenticatedFetch, '/admin/invites'),
    ...options,
  });
};

export const useAddInvite = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email) => apiRequestJson(authenticatedFetch, '/admin/invites', { email }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options,
  });
};

export const useRemoveInvite = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId) => apiRequestWithMethod(authenticatedFetch, `/admin/invites/${inviteId}`, 'DELETE'),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options,
  });
};

export const useTask = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUpdateActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, payload }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}`,
        'PUT',
        payload
      ),
    onSuccess: async (data, variables, context) => {
      const sourceTaskId = Number(variables?.taskId);
      const movedTaskId = Number(variables?.payload?.newTaskId ?? data?.taskId);

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (Number.isInteger(sourceTaskId)) {
        await queryClient.invalidateQueries({ queryKey: ['task', sourceTaskId] });
      }
      if (Number.isInteger(movedTaskId) && movedTaskId !== sourceTaskId) {
        await queryClient.invalidateQueries({ queryKey: ['task', movedTaskId] });
      }

      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });

      await invalidateGanttQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUser = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUserRates = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['userRates', userId],
    queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/rates`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useRates = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = projectId === null || projectId === undefined ? true : Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = Number.isInteger(projectId) ? `?projectId=${encodeURIComponent(String(projectId))}` : '';

  return useQuery({
    queryKey: ['rates', Number.isInteger(projectId) ? projectId : null],
    queryFn: () => apiRequest(authenticatedFetch, `/rates${qs}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

// Shared by the workplan hooks and usePrefetchUserWorkplanMonths, so a prefetch lands in exactly the
// cache entry the page later reads.
const userWorkplanQuery = (authenticatedFetch, userId, monthsParam) => ({
  queryKey: ['userWorkplan', userId, monthsParam],
  queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/workplan?months=${monthsParam}`),
});

const userWorkplanFlagsQuery = (authenticatedFetch, userId, monthsParam) => ({
  queryKey: ['userWorkplanFlags', userId, monthsParam],
  queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/workplan-flags?months=${monthsParam}`),
});

// Returns a function that warms the cache with a user's workplan and its flags for the given months,
// resolving once they are cached. It never rejects.
export const usePrefetchUserWorkplanMonths = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useCallback(
    (userId, months) => {
      if (!Number.isInteger(userId) || !Array.isArray(months) || months.length === 0) return Promise.resolve();
      const monthsParam = months.join(',');
      return Promise.all(
        [userWorkplanQuery, userWorkplanFlagsQuery].map((query) =>
          queryClient.prefetchQuery({ ...query(authenticatedFetch, userId, monthsParam), staleTime: 30000 })
        )
      );
    },
    [authenticatedFetch, queryClient]
  );
};

export const useUserWorkplan = (userId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId) && Array.isArray(months) && months.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  const monthsParam = months.join(',');

  return useQuery({
    ...userWorkplanQuery(authenticatedFetch, userId, monthsParam),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30000,
    ...restOptions,
  });
};

// Leads and existing flags for the workplan window. `enabled: false` in the response means
// the server has not run the flags migration; the page hides the feature in that case.
export const useUserWorkplanFlags = (userId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId) && Array.isArray(months) && months.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  const monthsParam = months.join(',');

  return useQuery({
    ...userWorkplanFlagsQuery(authenticatedFetch, userId, monthsParam),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30000,
    ...restOptions,
  });
};

// payload: { userId, taskId, monthStart, reason, message }
export const useCreateWorkplanFlag = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, ...body }) => apiRequestJson(authenticatedFetch, `/users/${userId}/workplan-flags`, body),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['userWorkplanFlags', variables?.userId] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useMyTimeOffDays = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['myTimeOffDays'],
    queryFn: () => apiRequest(authenticatedFetch, '/connect/employeeconnect/vacation-days/me'),
    enabled: optionsEnabled ?? true,
    staleTime: 60_000,
    ...restOptions,
  });
};

// Time off stays cached per user (['userTimeOffDays', id]), but every user requested in the same tick is
// fetched with one /time-off/users call, so a table of N people costs one round trip instead of N.
const TIME_OFF_BATCH_MAX_IDS = 200;
let pendingTimeOffBatch = null;

const flushTimeOffBatch = async ({ authenticatedFetch, waitersByUserId }) => {
  const settle = (userId, fn) => (waitersByUserId.get(userId) || []).forEach(fn);
  const ids = Array.from(waitersByUserId.keys());
  const chunks = [];
  for (let i = 0; i < ids.length; i += TIME_OFF_BATCH_MAX_IDS) chunks.push(ids.slice(i, i + TIME_OFF_BATCH_MAX_IDS));

  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const res = await apiRequest(authenticatedFetch, `/time-off/users?ids=${chunk.join(',')}`);
        const payloadById = new Map((res?.users || []).map((p) => [Number(p?.user_id), p]));
        const errorById = new Map((res?.errors || []).map((e) => [Number(e?.userId), e]));
        for (const userId of chunk) {
          if (payloadById.has(userId)) {
            settle(userId, (w) => w.resolve(payloadById.get(userId)));
          } else {
            const e = errorById.get(userId);
            const err = new Error(`HTTP ${e?.status ?? 500}: ${e?.message || 'Time off missing from batch response.'}`);
            settle(userId, (w) => w.reject(err));
          }
        }
      } catch (err) {
        for (const userId of chunk) settle(userId, (w) => w.reject(err));
      }
    })
  );
};

const fetchUserTimeOffBatched = (authenticatedFetch, userId) =>
  new Promise((resolve, reject) => {
    if (!pendingTimeOffBatch) {
      const batch = { authenticatedFetch, waitersByUserId: new Map() };
      pendingTimeOffBatch = batch;
      setTimeout(() => {
        pendingTimeOffBatch = null;
        flushTimeOffBatch(batch);
      }, 0);
    }
    const { waitersByUserId } = pendingTimeOffBatch;
    if (!waitersByUserId.has(userId)) waitersByUserId.set(userId, []);
    waitersByUserId.get(userId).push({ resolve, reject });
  });

export const useUserTimeOffDays = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const hasUserId = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['userTimeOffDays', hasUserId ? userId : null],
    queryFn: () => fetchUserTimeOffBatched(authenticatedFetch, userId),
    enabled: hasUserId && (optionsEnabled ?? true),
    staleTime: 60_000,
    ...restOptions,
  });
};

export const useLocalUserTimeOff = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const hasUserId = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['localUserTimeOff', hasUserId ? userId : null],
    queryFn: () => apiRequest(authenticatedFetch, `/time-off/user/${userId}`),
    enabled: hasUserId && (optionsEnabled ?? true),
    staleTime: 30_000,
    ...restOptions,
  });
};

export const useCreateLocalTimeOffRange = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, startDate, endDate, totalHours }) =>
      apiRequestJson(authenticatedFetch, '/time-off/range', { userId, startDate, endDate, totalHours }),
    onSuccess: async (data, variables, context) => {
      const uid = Number(variables?.userId);
      if (Number.isInteger(uid)) {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff', uid] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays', uid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff'] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'calendar'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteLocalTimeOffEntry = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ entryId }) => apiRequestWithMethod(authenticatedFetch, `/time-off/${entryId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      const uid = Number(variables?.userId);
      if (Number.isInteger(uid)) {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff', uid] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays', uid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff'] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'calendar'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateLocalTimeOffEntry = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ entryId, userId, date, hours }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/time-off/${entryId}`, 'PUT', { userId, date, hours }),
    onSuccess: async (data, variables, context) => {
      const uid = Number(variables?.userId);
      if (Number.isInteger(uid)) {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff', uid] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays', uid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff'] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'calendar'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useReplaceLocalTimeOffEntryRange = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ entryId, userId, startDate, endDate, totalHours }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/time-off/${entryId}/range`, 'PUT', { userId, startDate, endDate, totalHours }),
    onSuccess: async (data, variables, context) => {
      const uid = Number(variables?.userId);
      if (Number.isInteger(uid)) {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff', uid] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays', uid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff'] });
        await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'calendar'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTimeOffCalendar = (monthStart, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const normalizedMonthStart = String(monthStart || '').slice(0, 10);
  const hasMonthStart = /^\d{4}-\d{2}-\d{2}$/.test(normalizedMonthStart);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['timeOff', 'calendar', hasMonthStart ? normalizedMonthStart : null],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/time-off/calendar?monthStart=${encodeURIComponent(normalizedMonthStart)}`
      ),
    enabled: hasMonthStart && (optionsEnabled ?? true),
    staleTime: 30_000,
    ...restOptions,
  });
};

export const useUsersWorkplanForMonths = (userIds, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ids = Array.isArray(userIds)
    ? userIds.map((id) => Number(id)).filter((id) => Number.isInteger(id))
    : [];
  const normalizedMonths = Array.isArray(months)
    ? months.map((m) => String(m || '').slice(0, 10)).filter(Boolean)
    : [];
  const monthsParam = normalizedMonths.join(',');

  const { enabled: optionsEnabled, ...restOptions } = options || {};
  const enabled = (optionsEnabled ?? true) && ids.length > 0 && normalizedMonths.length > 0;

  return useQueries({
    queries: ids.map((userId) => ({
      queryKey: ['userWorkplan', userId, monthsParam],
      queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/workplan?months=${monthsParam}`),
      enabled,
      staleTime: 30_000,
      ...restOptions,
    })),
  });
};

export const useUsersTimeOffDays = (userIds, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ids = Array.isArray(userIds)
    ? userIds.map((id) => Number(id)).filter((id) => Number.isInteger(id))
    : [];

  const { enabled: optionsEnabled, ...restOptions } = options || {};
  const enabled = (optionsEnabled ?? true) && ids.length > 0;

  return useQueries({
    queries: ids.map((userId) => ({
      queryKey: ['userTimeOffDays', userId],
      queryFn: () => fetchUserTimeOffBatched(authenticatedFetch, userId),
      enabled,
      staleTime: 60_000,
      ...restOptions,
    })),
  });
};

export const useUpsertUserRate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  const mutation = useMutation({
    mutationFn: ({ userId, projectId, rate, effectiveDate }) =>
      apiRequestJson(authenticatedFetch, `/users/${userId}/rates`, { projectId, rate, effectiveDate }),
    onSuccess: async (data, variables, context) => {
      if (!variables?.silent) {
        await invalidateUserRateRelatedQueries(queryClient, Number(variables?.userId));
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });

  return {
    ...mutation,
    invalidateRelatedQueries: async ({ userId } = {}) => {
      await invalidateUserRateRelatedQueries(queryClient, Number(userId));
    },
  };
};

export const useDeleteUserRate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, projectId, effectiveDate }) => {
      const base = `/users/${userId}/rates/${projectId}`;
      if (effectiveDate) {
        return apiRequestWithMethod(
          authenticatedFetch,
          `${base}/${encodeURIComponent(String(effectiveDate).slice(0, 10))}`,
          'DELETE'
        );
      }
      return apiRequestWithMethod(authenticatedFetch, base, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      await invalidateUserRateRelatedQueries(queryClient, Number(variables?.userId));

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useAddActivitySupporter = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId }) =>
      apiRequestJson(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/supporters`, { userId }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals', variables.taskId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useSetActivitySupporters = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userIds }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/supporters`, 'PUT', {
        userIds,
      }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals', variables.taskId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId }) =>
      apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useSetActivityForecastHours = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, forecastHours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/forecast-hours`,
        'PUT',
        { forecastHours }
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Shared by useActivityHourForecast, the yearly-block hooks below and the prefetch helper, so a
// prefetched range lands in exactly the cache entry the table later reads.
const activityHourForecastWindowQuery = (authenticatedFetch, taskId, activityId, startMonth, count) => {
  const startKeyRaw = String(startMonth || '').slice(0, 10);
  const startKey = startKeyRaw.length >= 7 ? `${startKeyRaw.slice(0, 7)}-01` : startKeyRaw;

  return {
    queryKey: ['activityHourForecast', taskId, activityId, startKey, count],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast?start=${encodeURIComponent(
          startKey
        )}&count=${count}`
      ),
  };
};

const ACTIVITY_HOUR_FORECAST_STALE_TIME = 30_000;

export const useActivityHourForecast = (taskId, activityId, startMonth, count, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled =
    Number.isInteger(taskId) &&
    Number.isInteger(activityId) &&
    typeof startMonth === 'string' &&
    startMonth.length >= 10 &&
    Number.isInteger(count);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...activityHourForecastWindowQuery(authenticatedFetch, taskId, activityId, startMonth, count),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: ACTIVITY_HOUR_FORECAST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...restOptions,
  });
};

export const useActivityHourForecastForMonths = (taskId, activityId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastForMonths', taskId, activityId, monthsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...restOptions,
  });
};

// Activity forecasts can also be loaded as calendar-year blocks. Whatever months a view needs map onto
// the same few stable cache entries, so stepping through months reuses data already loaded instead of
// fetching a fresh range on every step.
const ACTIVITY_HOUR_FORECAST_BLOCK_MONTHS = 12;

const getActivityHourForecastBlockStarts = (months) => {
  const years = new Set();
  for (const m of Array.isArray(months) ? months : []) {
    const year = String(m || '').slice(0, 4);
    if (/^\d{4}$/.test(year)) years.add(year);
  }
  return Array.from(years).sort().map((year) => `${year}-01-01`);
};

// Stitches yearly blocks back into one response shaped like a single-range forecast. The roster
// doesn't depend on the month, so each user's per-month maps are simply combined.
const mergeActivityHourForecastBlocks = (blocks) => {
  const months = [];
  const rowsByUserId = new Map();
  for (const block of blocks) {
    months.push(...(Array.isArray(block?.months) ? block.months : []));
    for (const row of Array.isArray(block?.rows) ? block.rows : []) {
      const existing = rowsByUserId.get(row?.userId);
      if (!existing) {
        rowsByUserId.set(row?.userId, {
          ...row,
          rates: { ...row?.rates },
          rateEffectiveDates: { ...row?.rateEffectiveDates },
          hours: { ...row?.hours },
          committedHours: { ...row?.committedHours },
        });
        continue;
      }
      Object.assign(existing.rates, row?.rates);
      Object.assign(existing.rateEffectiveDates, row?.rateEffectiveDates);
      Object.assign(existing.hours, row?.hours);
      Object.assign(existing.committedHours, row?.committedHours);
    }
  }
  // Per-range fields (startMonth, count, totals) would only describe the first block, so drop them.
  // eslint-disable-next-line no-unused-vars
  const { startMonth, count, totals, ...meta } = blocks[0] ?? {};
  return { ...meta, months, rows: Array.from(rowsByUserId.values()) };
};

// Module-level so useQueries only re-runs it when a block's result actually changes.
const combineActivityHourForecastBlocks = (results) => {
  const ready = results.length > 0 && results.every((r) => r.data);
  return {
    data: ready ? mergeActivityHourForecastBlocks(results.map((r) => r.data)) : undefined,
    isLoading: results.some((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
    error: results.find((r) => r.error)?.error ?? null,
  };
};

// Forecast for the given months (any set, contiguous or not), loaded as calendar-year blocks.
// Returns { data, isLoading, isFetching, error }; data covers every month of each block touched.
export const useActivityHourForecastBlocks = (taskId, activityId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const blockStarts = getActivityHourForecastBlockStarts(months);
  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId) && (options.enabled ?? true);

  return useQueries({
    queries: blockStarts.map((blockStart) => ({
      ...activityHourForecastWindowQuery(
        authenticatedFetch,
        taskId,
        activityId,
        blockStart,
        ACTIVITY_HOUR_FORECAST_BLOCK_MONTHS
      ),
      enabled,
      staleTime: ACTIVITY_HOUR_FORECAST_STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
    combine: combineActivityHourForecastBlocks,
  });
};

// Returns a function that warms the cache with the yearly blocks covering `months`, resolving once
// they are cached. It never rejects; a failed prefetch just leaves the regular query to fetch (and
// report) it.
export const usePrefetchActivityHourForecastBlocks = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useCallback(
    ({ taskId, activityId, months }) => {
      if (!Number.isInteger(taskId) || !Number.isInteger(activityId)) return Promise.resolve();

      return Promise.all(
        getActivityHourForecastBlockStarts(months).map((blockStart) =>
          queryClient.prefetchQuery({
            ...activityHourForecastWindowQuery(
              authenticatedFetch,
              taskId,
              activityId,
              blockStart,
              ACTIVITY_HOUR_FORECAST_BLOCK_MONTHS
            ),
            staleTime: ACTIVITY_HOUR_FORECAST_STALE_TIME,
          })
        )
      );
    },
    [authenticatedFetch, queryClient]
  );
};

export const useTaskHourForecast = (taskId, startMonth, count, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled =
    Number.isInteger(taskId) &&
    typeof startMonth === 'string' &&
    startMonth.length >= 10 &&
    Number.isInteger(count);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskHourForecast', taskId, String(startMonth).slice(0, 10), count],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/hour-forecast?start=${encodeURIComponent(String(startMonth).slice(0, 10))}&count=${count}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

// Query definitions shared by the month-list hooks below and the actuals prefetch helpers, so a
// prefetch lands in exactly the cache entry the page later reads.
const taskHourForecastForMonthsQuery = (authenticatedFetch, taskId, monthsKey) => ({
  queryKey: ['taskHourForecastForMonths', taskId, monthsKey],
  queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
});

const taskActualsForMonthsQuery = (authenticatedFetch, taskId, monthsKey) => ({
  queryKey: ['taskActualsForMonths', taskId, monthsKey],
  queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/actuals?months=${encodeURIComponent(monthsKey)}`),
});

const taskOrderHourForecastForMonthsQuery = (authenticatedFetch, projectId, taskOrderId, monthsKey) => ({
  queryKey: ['taskOrderHourForecastForMonths', projectId, taskOrderId, monthsKey],
  queryFn: () =>
    apiRequest(
      authenticatedFetch,
      `/projects/${projectId}/task-orders/${taskOrderId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`
    ),
});

const taskOrderActualsForMonthsQuery = (authenticatedFetch, projectId, taskOrderId, monthsKey) => ({
  queryKey: ['taskOrderActualsForMonths', projectId, taskOrderId, monthsKey],
  queryFn: () =>
    apiRequest(
      authenticatedFetch,
      `/projects/${projectId}/task-orders/${taskOrderId}/actuals?months=${encodeURIComponent(monthsKey)}`
    ),
});

const taskOrderTaskMonthRollupQuery = (authenticatedFetch, projectId, taskOrderId, monthsKey) => ({
  queryKey: ['taskOrderTaskMonthRollup', projectId, taskOrderId, monthsKey],
  queryFn: () =>
    apiRequest(
      authenticatedFetch,
      `/projects/${projectId}/task-orders/${taskOrderId}/task-month-rollup?months=${encodeURIComponent(monthsKey)}`
    ),
});

// The task hooks normalise each month to YYYY-MM-01; the task order hooks use the list as given.
const normalizeTaskMonthsKey = (months) =>
  (Array.isArray(months) ? months : [])
    .map((m) => String(m || '').slice(0, 10))
    .filter(Boolean)
    .map((s) => `${s.slice(0, 7)}-01`)
    .join(',');

const plainMonthsKey = (months) => (Array.isArray(months) ? months.filter(Boolean).join(',') : '');

// Prefetched month lists stay fresh this long, so warming the cache ahead of month stepping doesn't
// refetch what is already loaded.
const ACTUALS_PREFETCH_STALE_TIME = 30_000;

// Returns a function that warms the cache with the task actuals page's forecast and actuals for the
// given months, resolving once they are cached. It never rejects.
export const usePrefetchTaskActualsMonths = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useCallback(
    (taskId, months) => {
      const monthsKey = normalizeTaskMonthsKey(months);
      if (!Number.isInteger(taskId) || !monthsKey) return Promise.resolve();
      return Promise.all(
        [taskHourForecastForMonthsQuery, taskActualsForMonthsQuery].map((query) =>
          queryClient.prefetchQuery({
            ...query(authenticatedFetch, taskId, monthsKey),
            staleTime: ACTUALS_PREFETCH_STALE_TIME,
          })
        )
      );
    },
    [authenticatedFetch, queryClient]
  );
};

// Same as usePrefetchTaskActualsMonths, for the task order actuals page's forecast, actuals and
// discipline breakdown.
export const usePrefetchTaskOrderActualsMonths = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useCallback(
    (projectId, taskOrderId, months) => {
      const monthsKey = plainMonthsKey(months);
      if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId) || !monthsKey) return Promise.resolve();
      return Promise.all(
        [taskOrderHourForecastForMonthsQuery, taskOrderActualsForMonthsQuery, taskOrderTaskMonthRollupQuery].map(
          (query) =>
            queryClient.prefetchQuery({
              ...query(authenticatedFetch, projectId, taskOrderId, monthsKey),
              staleTime: ACTUALS_PREFETCH_STALE_TIME,
            })
        )
      );
    },
    [authenticatedFetch, queryClient]
  );
};

export const useTaskHourForecastForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');

  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...taskHourForecastForMonthsQuery(authenticatedFetch, taskId, monthsKey),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskActivitiesHourForecast = (taskId, startMonth, count, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const startKeyRaw = String(startMonth || '').slice(0, 10);
  const startKey = startKeyRaw.length >= 7 ? `${startKeyRaw.slice(0, 7)}-01` : startKeyRaw;

  const enabled =
    Number.isInteger(taskId) &&
    typeof startMonth === 'string' &&
    startMonth.length >= 10 &&
    Number.isInteger(count);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskActivitiesHourForecast', taskId, startKey, count],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/activities/hour-forecast?start=${encodeURIComponent(startKey)}&count=${count}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...restOptions,
  });
};

export const useTaskActivitiesHourForecastTotals = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskActivitiesHourForecastTotals', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/hour-forecast-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskActualsForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');
  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...taskActualsForMonthsQuery(authenticatedFetch, taskId, monthsKey),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcMonthlyForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');
  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcMonthlyForMonths', taskId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-monthly?months=${encodeURIComponent(monthsKey)}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcActivityMonthlyForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');
  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcActivityMonthlyForMonths', taskId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-monthly-activities?months=${encodeURIComponent(monthsKey)}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcMonthlyTotals = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcMonthlyTotals', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-monthly-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcActivityTotals = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcActivityTotals', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-activity-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useBulkUpsertTaskOdcMonthly = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, edits }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/odc-monthly-bulk`,
        'PUT',
        { edits }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityTotals', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useBulkUpsertTaskOdcActivityMonthly = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, edits }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/odc-monthly-activities-bulk`,
        'PUT',
        { edits }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityMonthlyForMonths', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityTotals', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityMonthlyForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcActivityTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertTaskActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, monthStart, rows }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}/actuals`, 'PUT', { monthStart, rows }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths', variables.taskId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });

      // Actual edits affect project charts.
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useImportActuals = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ entries, projectId, taskOrderId }) =>
      apiRequestJson(authenticatedFetch, '/actuals/import', {
        entries,
        projectId,
        taskOrderId,
      }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearTaskActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}/actuals/${encodeURIComponent(ms)}`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths', variables.taskId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });

      // Clearing actuals affects project rollups and charts too.
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useActivityHourForecastMonthsWithHours = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastMonthsWithHours', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/hour-forecast-months-with-hours`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useActivityHourForecastTotals = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastTotals', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/hour-forecast-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useActivityActualsMonthlyTotals = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityActualsMonthlyTotals', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/actuals-monthly-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUpsertActivityActuals = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId, monthStart, hours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/actuals`,
        'PUT',
        { userId, monthStart, hours }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['activityActualsMonthlyTotals', variables.taskId, variables.activityId],
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityActualsMonthlyTotals'] });
      }

      // Actual edits affect project charts.
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertActivityHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId, monthStart, hours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast`,
        'PUT',
        { userId, monthStart, hours }
      ),
    onSuccess: async (data, variables, context) => {
      // Update any cached month windows for this activity.
      if (variables?.taskId != null && variables?.activityId != null && variables?.userId != null && variables?.monthStart) {
        const monthStart = String(variables.monthStart).slice(0, 10);
        const nextHours = Number.isFinite(Number(variables.hours)) ? Number(variables.hours) : 0;
        const userId = Number(variables.userId);
        if (Number.isInteger(userId)) {
          queryClient.setQueriesData(
            { queryKey: ['activityHourForecast', variables.taskId, variables.activityId] },
            (prev) => {
              if (!prev || typeof prev !== 'object') return prev;
              const prevRows = Array.isArray(prev.rows) ? prev.rows : [];
              const nextRows = prevRows.map((r) => {
                const rid = Number(r?.userId);
                if (!Number.isInteger(rid) || rid !== userId) return r;
                const prevHours = r?.hours && typeof r.hours === 'object' ? r.hours : {};
                return {
                  ...r,
                  hours: {
                    ...prevHours,
                    [monthStart]: nextHours,
                  },
                };
              });

              const prevTotals = prev.totals && typeof prev.totals === 'object' ? prev.totals : {};
              let nextTotalsValue = 0;
              for (const r of nextRows) {
                const hoursMap = r?.hours && typeof r.hours === 'object' ? r.hours : {};
                nextTotalsValue += Number(hoursMap?.[monthStart] ?? 0);
              }

              return {
                ...prev,
                rows: nextRows,
                totals: {
                  ...prevTotals,
                  [monthStart]: nextTotalsValue,
                },
              };
            }
          );
        }
      }

      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals', variables.taskId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      // View-all totals on the forecast page are driven by this project-level summary.
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });
      await invalidateGanttQueries(queryClient);

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useBulkUpsertActivityHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, edits }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast-bulk`,
        'PUT',
        { edits }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals', variables.taskId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['taskActivitiesHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await invalidateGanttQueries(queryClient);

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};


export const useShiftTaskHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, deltaMonths }) =>
      apiRequestJson(authenticatedFetch, `/tasks/${taskId}/hour-forecast/shift`, { deltaMonths }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });
      await invalidateGanttQueries(queryClient);

      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};
export const useProjectActivityForecastSummary = (projectId, rangeOrYear = null, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const range =
    typeof rangeOrYear === 'number' && Number.isInteger(rangeOrYear)
      ? { from: `${rangeOrYear}-01-01`, to: `${rangeOrYear + 1}-01-01` }
      : (rangeOrYear && typeof rangeOrYear === 'object' ? rangeOrYear : {});

  const from = typeof range?.from === 'string' ? String(range.from).slice(0, 10) : null;
  const to = typeof range?.to === 'string' ? String(range.to).slice(0, 10) : null;

  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : '';

  return useQuery({
    queryKey: ['projectActivityForecastSummary', projectId, from, to],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/activity-forecast-summary${qs}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectGantt = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectGantt', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/gantt`),
    enabled: Number.isInteger(projectId) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderGantt = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderGantt', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/gantt`),
    enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

const normalizeChartInterval = (interval) => {
  const v = String(interval || 'month').toLowerCase();
  if (v === 'month' || v === 'quarter' || v === 'year') return v;
  return 'month';
};

const normalizeDate10 = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).slice(0, 10);
  return s.length === 10 ? s : null;
};

const buildChartsQs = (interval, startDate, endDate, taskOrderId = null, includePursuits = null) => {
  const params = new URLSearchParams();
  if (interval) params.set('interval', interval);
  if (startDate) params.set('start', startDate);
  if (endDate) params.set('end', endDate);
  if (Number.isInteger(taskOrderId) && taskOrderId > 0) params.set('task_order_id', String(taskOrderId));
  if (typeof includePursuits === 'boolean') params.set('includePursuits', includePursuits ? 'true' : 'false');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const useProjectForecastedHours = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectForecastedHours', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectForecastedRevenue = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectForecastedRevenue', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

// Projected spend by period (actuals for months that are over, forecast ahead, plus
// ODCs). Same figures as the Staffing Outlook; always all-time, for the burndown.
export const useProjectProjectedSpend = (projectId, interval, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);
  const qs = buildChartsQs(safeInterval, null, null, taskOrderId);

  return useQuery({
    queryKey: ['projectProjectedSpend', projectId, safeInterval, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/projected-spend${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Number.isInteger(projectId) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectHoursByUser = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectHoursByUser', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/hours-by-user${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectChartDateSpan = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);
  const qs = Number.isInteger(taskOrderId) ? `?task_order_id=${encodeURIComponent(String(taskOrderId))}` : '';

  return useQuery({
    queryKey: ['projectChartDateSpan', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/date-span${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Number.isInteger(projectId) && (optionsEnabled ?? true),
    staleTime: 60_000,
    ...restOptions,
  });
};

const normalizeProjectIds = (projectIds) => {
  const ids = Array.isArray(projectIds) ? projectIds : [];
  return ids.map((id) => Number(id)).filter((n) => Number.isInteger(n));
};

export const useProjectsForecastedHours = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectForecastedHours', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-hours${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectsForecastedRevenue = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectForecastedRevenue', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-revenue${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectActualRevenue = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectActualRevenue', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsActualRevenue = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectActualRevenue', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-revenue${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectInvoicedAmount = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectInvoicedAmount', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/invoiced-amount${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsInvoicedAmount = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectInvoicedAmount', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/invoiced-amount${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectsHoursByUser = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectHoursByUser', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/hours-by-user${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useAllProjectsForecastedHours = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsForecastedHours', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/forecasted-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsActualHours = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsActualHours', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/actual-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsForecastedRevenue = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  // Omitting both dates requests the unbounded (all-time) series; a partial range is not valid.
  const hasValidRange = Boolean(start && end) || (!start && !end);

  return useQuery({
    queryKey: ['allProjectsForecastedRevenue', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/forecasted-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: hasValidRange && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsActualRevenue = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsActualRevenue', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/actual-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsInvoicedAmount = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsInvoicedAmount', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/invoiced-amount${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsHoursByUser = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsHoursByUser', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/hours-by-user${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsTotalBudget = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = typeof includePursuits === 'boolean'
    ? `?includePursuits=${includePursuits ? 'true' : 'false'}`
    : '';

  return useQuery({
    queryKey: ['allProjectsTotalBudget', includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/total-budget${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsChartsSummary = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end, null, includePursuits);

  return useQuery({
    queryKey: ['allProjectsChartsSummary', safeInterval, start, end, includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/summary${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCloseDates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useCloseDateSources = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates', 'sources'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates/sources'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useActiveCloseDateSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates', 'source'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates/source'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetActiveCloseDateSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ sourceName }) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/close-dates/source', 'PUT', { sourceName }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      await queryClient.invalidateQueries({ queryKey: ['closeDates', 'source'] });
      await queryClient.invalidateQueries({ queryKey: ['closeDates', 'sources'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTimeOffSources = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['timeOff', 'sources'],
    queryFn: () => apiRequest(authenticatedFetch, '/time-off/sources'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useActiveTimeOffSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['timeOff', 'source'],
    queryFn: () => apiRequest(authenticatedFetch, '/time-off/source'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetActiveTimeOffSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ sourceName }) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/time-off/source', 'PUT', { sourceName }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'source'] });
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'sources'] });
      await queryClient.invalidateQueries({ queryKey: ['timeOff', 'calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['localUserTimeOff'] });
      await queryClient.invalidateQueries({ queryKey: ['userTimeOffDays'] });
      await queryClient.invalidateQueries({ queryKey: ['myTimeOffDays'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertCloseDate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/close-dates', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteCloseDate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (id) => apiRequestWithMethod(authenticatedFetch, `/close-dates/${id}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProviders = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['providers'],
    queryFn: () => apiRequest(authenticatedFetch, '/providers'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useActiveProvider = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['providers', 'active'],
    queryFn: () => apiRequest(authenticatedFetch, '/providers/active'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetActiveProvider = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ providerName, config }) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/providers/active', 'PUT', { providerName, config }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['providers', 'active'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Deactivates the active hours provider and clears its stored credentials.
export const useDisconnectActiveProvider = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: () => apiRequestJsonWithMethod(authenticatedFetch, '/providers/active', 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['providers', 'active'] });
      await queryClient.invalidateQueries({ queryKey: ['providers'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateProviderConfig = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ providerName, config }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/providers/${providerName}/config`, 'PUT', { config }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['providers', 'active'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}`, 'PUT', payload),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);

      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        // A new PIC is added to the project's people on the server.
        if (variables?.payload?.picUserId !== undefined) {
          await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        }
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestWithMethod(authenticatedFetch, `/projects/${projectId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useRestoreProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestJson(authenticatedFetch, `/projects/${projectId}/restore`, {}),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const usePermanentDeleteProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestWithMethod(authenticatedFetch, `/projects/${projectId}/permanent`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectUsers = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectUsers', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/users`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

// What a person is tied to on a project (forecast hours, disciplines/activities they
// lead or support). Drives how strongly the "remove from project" confirmation warns.
export const useProjectUserAssignmentSummary = (projectId, userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectUserAssignmentSummary', projectId, userId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/users/${userId}/assignment-summary`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Number.isInteger(projectId) && Number.isInteger(userId) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

// After the member list changes: the list itself, the projects' people counts, and /me, since the
// signed-in user's own rights may be what changed.
const invalidateProjectMembers = (queryClient, projectId) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['projectUsers', Number(projectId)] }),
    queryClient.invalidateQueries({ queryKey: ['projects'] }),
    queryClient.invalidateQueries({ queryKey: ['me'] }),
  ]);

// Sets the whole member list. `rights` ({ [userId]: string[] }) is optional: members already on the
// project keep theirs, and new ones without an entry can only view.
export const useSetProjectUsers = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, userIds, rights }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}/users`, 'PUT', { userIds, ...(rights ? { rights } : {}) }),
    onSuccess: async (data, variables, context) => {
      await invalidateProjectMembers(queryClient, variables?.projectId);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Adds one person to a project with these rights, or changes the rights of a member.
export const useSetProjectMember = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, userId, rights }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}/users/${userId}`, 'PUT', { rights }),
    onSuccess: async (data, variables, context) => {
      await invalidateProjectMembers(queryClient, variables?.projectId);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useRemoveProjectMember = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, userId }) =>
      apiRequestWithMethod(authenticatedFetch, `/projects/${projectId}/users/${userId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await invalidateProjectMembers(queryClient, variables?.projectId);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectActualHours = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, taskOrderId: rawTaskOrderId, ...restOptions } = options;
  const taskOrderId = normalizeTaskOrderId(rawTaskOrderId);

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end, taskOrderId);

  return useQuery({
    queryKey: ['projectActualHours', projectId, safeInterval, start, end, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsActualHours = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectActualHours', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-hours${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectHourForecastForMonths = (projectId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectHourForecastForMonths', projectId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderHourForecastForMonths = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...taskOrderHourForecastForMonthsQuery(authenticatedFetch, projectId, taskOrderId, monthsKey),
    enabled:
      (optionsEnabled ?? true) &&
      Number.isInteger(projectId) &&
      Number.isInteger(taskOrderId) &&
      !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectActualsForMonths = (projectId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectActualsForMonths', projectId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/actuals?months=${encodeURIComponent(monthsKey)}`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderActualsForMonths = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...taskOrderActualsForMonthsQuery(authenticatedFetch, projectId, taskOrderId, monthsKey),
    enabled:
      (optionsEnabled ?? true) &&
      Number.isInteger(projectId) &&
      Number.isInteger(taskOrderId) &&
      !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

// What counts as "on pace" in the actuals Pace view, per project.
export const useProjectPaceSettings = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectPaceSettings', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/pace-settings`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId),
    staleTime: 60_000,
    ...restOptions,
  });
};

export const useUpdateProjectPaceSettings = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, ...body }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}/pace-settings`, 'PUT', body),
    onSuccess: async (data, variables, context) => {
      queryClient.setQueryData(['projectPaceSettings', variables.projectId], data);
      if (onSuccess) await onSuccess(data, variables, context);
    },
    ...restOptions,
  });
};

// ---- Staffing scenarios (task order level what-ifs) ----

const scenariosBase = (projectId, taskOrderId) => `/projects/${projectId}/task-orders/${taskOrderId}/scenarios`;

export const useTaskOrderScenarios = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderScenarios', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, scenariosBase(projectId, taskOrderId)),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderScenario = (projectId, taskOrderId, scenarioId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderScenario', projectId, taskOrderId, scenarioId],
    queryFn: () => apiRequest(authenticatedFetch, `${scenariosBase(projectId, taskOrderId)}/${scenarioId}`),
    enabled:
      (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId) && Number.isInteger(scenarioId),
    staleTime: 0,
    ...restOptions,
  });
};

// Availability, current forecast by discipline and rates for the people in a scenario.
export const useScenarioContext = (projectId, taskOrderId, startMonth, endMonth, userIds, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const ids = Array.isArray(userIds)
    ? Array.from(new Set(userIds.map(Number).filter((id) => Number.isInteger(id)))).sort((a, b) => a - b)
    : [];
  const idsKey = ids.join(',');

  return useQuery({
    queryKey: ['scenarioContext', projectId, taskOrderId, startMonth, endMonth, idsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/scenario-context?start=${startMonth}&end=${endMonth}&userIds=${idsKey}`
      ),
    enabled:
      (optionsEnabled ?? true)
      && Number.isInteger(projectId)
      && Number.isInteger(taskOrderId)
      && !!startMonth
      && !!endMonth,
    staleTime: 30_000,
    placeholderData: (previous) => previous,
    ...restOptions,
  });
};

const useScenarioMutation = (buildRequest, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (vars) => buildRequest(authenticatedFetch, vars),
    onSuccess: async (data, vars, context) => {
      await queryClient.invalidateQueries({ queryKey: ['taskOrderScenarios', vars.projectId, vars.taskOrderId] });
      if (data?.id) queryClient.setQueryData(['taskOrderScenario', vars.projectId, vars.taskOrderId, data.id], data);
      if (onSuccess) await onSuccess(data, vars, context);
    },
    ...restOptions,
  });
};

export const useCreateTaskOrderScenario = (options = {}) =>
  useScenarioMutation(
    (fetcher, { projectId, taskOrderId, ...body }) => apiRequestJson(fetcher, scenariosBase(projectId, taskOrderId), body),
    options
  );

export const useUpdateTaskOrderScenario = (options = {}) =>
  useScenarioMutation(
    (fetcher, { projectId, taskOrderId, scenarioId, ...body }) =>
      apiRequestJsonWithMethod(fetcher, `${scenariosBase(projectId, taskOrderId)}/${scenarioId}`, 'PUT', body),
    options
  );

export const useDeleteTaskOrderScenario = (options = {}) =>
  useScenarioMutation(
    (fetcher, { projectId, taskOrderId, scenarioId }) =>
      apiRequestWithMethod(fetcher, `${scenariosBase(projectId, taskOrderId)}/${scenarioId}`, 'DELETE'),
    options
  );

// Emails a discipline's scenario instructions to people picked from its contacts. Sends only; nothing to invalidate.
export const useEmailScenarioLead = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useMutation({
    mutationFn: ({ projectId, taskOrderId, scenarioId, ...body }) =>
      apiRequestJson(authenticatedFetch, `${scenariosBase(projectId, taskOrderId)}/${scenarioId}/email-lead`, body),
    ...options,
  });
};

// Per-person hours and rates by month, ODC dollars and the budget, for the staffing outlook.
export const useTaskOrderStaffingOutlook = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderStaffingOutlook', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/staffing-outlook`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderMonthTaskRollup = (projectId, taskOrderId, monthStart, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ms = typeof monthStart === 'string' ? monthStart.slice(0, 10) : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderMonthTaskRollup', projectId, taskOrderId, ms],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/month-task-rollup?month=${encodeURIComponent(ms)}`
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId) && !!ms,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderTaskMonthRollup = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    ...taskOrderTaskMonthRollupQuery(authenticatedFetch, projectId, taskOrderId, monthsKey),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderActualDollarsByTask = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderActualDollarsByTask', projectId, taskOrderId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/actual-dollars-by-task`
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderTaskSpendingByMonth = (projectId, taskOrderId, taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderTaskSpendingByMonth', projectId, taskOrderId, taskId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/spending-by-month`
      ),
    enabled:
      (optionsEnabled ?? true)
      && Number.isInteger(projectId)
      && Number.isInteger(taskOrderId)
      && Number.isInteger(taskId),
    staleTime: 5 * 60 * 1000,
    ...restOptions,
  });
};

export const useTaskOrderTasksSpendingByMonth = (projectId, taskOrderId, taskIds, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ids = Array.isArray(taskIds)
    ? taskIds.map((id) => Number(id)).filter((id) => Number.isInteger(id))
    : [];
  const { enabled: optionsEnabled, ...restOptions } = options;
  const enabledBase =
    (optionsEnabled ?? true)
    && Number.isInteger(projectId)
    && Number.isInteger(taskOrderId)
    && ids.length > 0;

  return useQueries({
    queries: ids.map((taskId) => ({
      queryKey: ['taskOrderTaskSpendingByMonth', projectId, taskOrderId, taskId],
      queryFn: () =>
        apiRequest(
          authenticatedFetch,
          `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/spending-by-month`
        ),
      enabled: enabledBase,
      staleTime: 5 * 60 * 1000,
      ...restOptions,
    })),
  });
};

export const useTaskOrderInvoices = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderInvoices', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/invoices`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, payload }) =>
      apiRequestJson(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/invoices`, payload),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['allProjectsInvoicedAmount'] });
      await queryClient.invalidateQueries({ queryKey: ['allProjectsChartsSummary'] });

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId }) =>
      apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}`,
        'DELETE'
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['allProjectsInvoicedAmount'] });
      await queryClient.invalidateQueries({ queryKey: ['allProjectsChartsSummary'] });

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId, payload }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}`,
        'PUT',
        payload
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['allProjectsInvoicedAmount'] });
      await queryClient.invalidateQueries({ queryKey: ['allProjectsChartsSummary'] });

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearProjectActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/actuals/${encodeURIComponent(ms)}`,
        'DELETE'
      );
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.projectId != null) {
        await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths', variables.projectId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });

      // Clearing actuals affects task-level actuals queries and project charts.
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearTaskOrderActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/actuals/${encodeURIComponent(ms)}`,
        'DELETE'
      );
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.projectId != null && variables?.taskOrderId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['taskOrderActualsForMonths', variables.projectId, variables.taskOrderId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['taskOrderHourForecastForMonths', variables.projectId, variables.taskOrderId],
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOrderActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOrderHourForecastForMonths'] });

      // Keep older query keys reasonably fresh if they are still used anywhere.
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });

      // Clearing actuals affects task-level actuals queries and project charts.
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (taskId) => apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables != null) {
        await queryClient.removeQueries({ queryKey: ['task', variables] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectsMilestones = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, includePursuits = false, ...restOptions } = options;

  const qs = typeof includePursuits === 'boolean'
    ? `?includePursuits=${includePursuits ? 'true' : 'false'}`
    : '';

  return useQuery({
    queryKey: ['milestones', 'allProjects', includePursuits],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/milestones${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useArchiveProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestJson(authenticatedFetch, `/projects/${projectId}/archive`, {}),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUnarchiveProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestJson(authenticatedFetch, `/projects/${projectId}/unarchive`, {}),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};
// ----- Actuals email alerts -----

// `taskId` scopes the list to one discipline's alerts; omit it (or pass null) for
// the task order's own alerts. The two scopes are separate lists server-side.
export const useActualsEmailAlerts = (projectId, taskOrderId, taskId = null, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const scopedTaskId = Number.isInteger(taskId) ? taskId : null;

  return useQuery({
    queryKey: ['actualsEmailAlerts', projectId, taskOrderId, scopedTaskId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/email-alerts?projectId=${encodeURIComponent(projectId)}&taskOrderId=${encodeURIComponent(taskOrderId)}` +
          (scopedTaskId === null ? '' : `&taskId=${encodeURIComponent(scopedTaskId)}`)
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateActualsEmailAlert = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/email-alerts', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['actualsEmailAlerts'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateActualsEmailAlert = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/email-alerts/${id}`, 'PATCH', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['actualsEmailAlerts'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteActualsEmailAlert = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (id) => apiRequestWithMethod(authenticatedFetch, `/email-alerts/${id}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['actualsEmailAlerts'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// ----- Current user's UI preferences (theme) -----

// Scope the cache to a stable per-session identity (auth userId) so a sign-out/sign-in
// without a full reload can't serve the previous user's preference to the new user.
const myThemePreferenceKey = (userId) => ['myThemePreference', userId ?? null];

export const useMyThemePreference = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: myThemePreferenceKey(userId),
    queryFn: () => apiRequest(authenticatedFetch, '/users/me/preferences'),
    enabled: Boolean(userId) && (optionsEnabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    ...restOptions,
  });
};

export const useUpdateMyThemePreference = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (theme) => apiRequestJsonWithMethod(authenticatedFetch, '/users/me/preferences', 'PUT', { theme }),
    onSuccess: async (data, variables, context) => {
      queryClient.setQueryData(myThemePreferenceKey(userId), (prev) => ({ ...(prev || {}), ...(data || {}) }));
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Projects page card/list choice. Shares the preferences endpoint (and its cache
// entry) with the theme, so reading it costs no extra request.
export const useMyProjectsViewPreference = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: myThemePreferenceKey(userId),
    queryFn: () => apiRequest(authenticatedFetch, '/users/me/preferences'),
    enabled: Boolean(userId) && (optionsEnabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    ...restOptions,
  });
};

export const useUpdateMyProjectsViewPreference = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectsView) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/users/me/preferences', 'PUT', { projectsView }),
    onSuccess: async (data, variables, context) => {
      queryClient.setQueryData(myThemePreferenceKey(userId), (prev) => ({ ...(prev || {}), ...(data || {}) }));
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Projects the signed-in user has hidden from their own projects list. `enabled: false` means the
// server has not run the user_hidden_projects migration; the page hides the option in that case.
const myHiddenProjectsKey = (userId) => ['myHiddenProjects', userId ?? null];

export const useMyHiddenProjects = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: myHiddenProjectsKey(userId),
    queryFn: () => apiRequest(authenticatedFetch, '/users/me/hidden-projects'),
    enabled: Boolean(userId) && (optionsEnabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    ...restOptions,
  });
};

// Hide ({ projectId, hidden: true }) or show again ({ projectId, hidden: false }) a project for the
// signed-in user. The list updates straight away and rolls back if the request fails.
export const useSetProjectHiddenForMe = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;
  const key = myHiddenProjectsKey(userId);

  return useMutation({
    mutationFn: ({ projectId, hidden }) =>
      apiRequestWithMethod(authenticatedFetch, `/users/me/hidden-projects/${projectId}`, hidden ? 'PUT' : 'DELETE'),
    onMutate: async ({ projectId, hidden }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (prev) => {
        const ids = (prev?.projectIds || []).filter((id) => id !== projectId);
        return { ...(prev || {}), enabled: true, projectIds: hidden ? [projectId, ...ids] : ids };
      });
      return { previous };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(key, context?.previous);
      return onError?.(error, variables, context);
    },
    onSuccess: async (data, variables, context) => {
      if (data) queryClient.setQueryData(key, data);
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// Every employee's current hours commitment from EmployeeConnect (read-only), relayed by ProPlan's
// server because EC's CORS policy only admits its own frontend. Each employee carries `proplan_user`
// (the matched ProPlan user, or null). Errors carry EC's status and message, e.g. why a 403 was refused.
export const useEmployeeConnectCommitments = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['employeeConnectCommitments'],
    queryFn: async () => {
      const response = await authenticatedFetch(buildApiUrl('/connect/employeeconnect/commitments'));
      const text = await response.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }
      if (!response.ok) {
        const err = new Error(String(json?.message || `EmployeeConnect request failed (${response.status}).`));
        err.status = response.status;
        err.code = json?.error ?? null;
        throw err;
      }
      return json;
    },
    enabled: optionsEnabled ?? true,
    retry: false,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    ...restOptions,
  });
};

// --- Reserved hours from EmployeeConnect TAWH (User Management) ---------------------------------

// Like apiRequestJsonWithMethod, but the thrown error carries the server's own message.
const requestJsonWithServerError = async (authenticatedFetch, endpoint, method, body) => {
  const response = await authenticatedFetch(buildApiUrl(endpoint), {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    const err = new Error(String(json?.error || json?.message || `Request failed (${response.status}).`));
    err.status = response.status;
    err.payload = json;
    throw err;
  }
  return json;
};

// { available, useTawh, adminPercent, lastSyncedAt, lastSyncSummary }; available is false until the
// server has run migration 012.
export const useReservedHoursSettings = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery({
    queryKey: ['reservedHoursSettings'],
    queryFn: () => apiRequest(authenticatedFetch, '/reserved-hours/settings'),
    staleTime: 30 * 1000,
    ...options,
  });
};

// Saves { useTawh?, adminPercent? }. When on, the server recalculates straight away; the response
// carries { sync } or, if EmployeeConnect could not be read, { syncError }.
export const useUpdateReservedHoursSettings = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: (payload) => requestJsonWithServerError(authenticatedFetch, '/reserved-hours/settings', 'PUT', payload),
    onSuccess: async (data, variables, context) => {
      queryClient.setQueryData(['reservedHoursSettings'], data);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// The Recalculate button: sets reserved hours from EmployeeConnect now.
export const useRecalculateReservedHours = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, onError, ...restOptions } = options;
  return useMutation({
    mutationFn: () => requestJsonWithServerError(authenticatedFetch, '/reserved-hours/recalculate', 'POST', {}),
    onSuccess: async (data, variables, context) => {
      queryClient.setQueryData(['reservedHoursSettings'], data);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    onError: async (error, variables, context) => {
      // A failed run is recorded server-side; refresh so the status line shows it.
      await queryClient.invalidateQueries({ queryKey: ['reservedHoursSettings'] });
      return onError?.(error, variables, context);
    },
    ...restOptions,
  });
};

// Signed, time-limited URL for a tutorial video in blob storage. The URL is
// refreshed well before the server-side expiry so a page left open keeps
// working; a missing video is an expected state, so it is not retried.
export const useHelpVideoUrl = (videoKey, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const key = String(videoKey || '').trim();

  return useQuery({
    queryKey: ['help', 'videoUrl', key],
    queryFn: () => apiRequest(authenticatedFetch, `/help/videos/url?key=${encodeURIComponent(key)}`),
    enabled: Boolean(key) && (optionsEnabled ?? true),
    retry: false,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...restOptions,
  });
};

// --- EmployeeConnect timesheet link (project page) ---------------------------------------------
//
// A project links to an EmployeeConnect timesheet project, and its task orders to child tasks under
// it. EmployeeConnect stores the link; ProPlan's server reads and changes it there. Errors carry the
// server's message (e.g. "EmployeeConnect is not configured on this server").

// True when the server can reach EmployeeConnect (Connect Suite on, API url and app credentials set).
export const useHasEmployeeConnect = (options = {}) => {
  const { data } = useRuntimeFeatureFlags(options);
  return data?.hasEmployeeConnect === true;
};

const timesheetRequest = async (authenticatedFetch, endpoint, method = 'GET', body) => {
  const response = await authenticatedFetch(buildApiUrl(endpoint), {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!response.ok) {
    const err = new Error(String(json?.message || json?.error || `Request failed (${response.status}).`));
    err.status = response.status;
    err.code = json?.error ?? null;
    throw err;
  }
  return json;
};

const timesheetQueryOptions = {
  retry: false,
  staleTime: 30 * 1000,
  refetchOnWindowFocus: false,
};

// { project, taskOrders: [{ taskOrderId, task }] }: the linked EmployeeConnect project (or null) and
// the child task linked to each task order that has one.
export const useProjectTimesheet = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const pid = Number(projectId);

  return useQuery({
    queryKey: ['projectTimesheet', pid],
    queryFn: () => timesheetRequest(authenticatedFetch, `/projects/${pid}/timesheet`),
    enabled: Number.isInteger(pid) && (optionsEnabled ?? true),
    ...timesheetQueryOptions,
    ...restOptions,
  });
};

// Unlinked top-level EmployeeConnect projects, or with a taskOrderId the unlinked tasks under the
// project's linked EmployeeConnect project.
export const useTimesheetLinkCandidates = ({ projectId, taskOrderId = null }, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const pid = Number(projectId);
  const toid = normalizeTaskOrderId(taskOrderId);
  const endpoint = toid
    ? `/projects/${pid}/task-orders/${toid}/timesheet/candidates`
    : `/projects/${pid}/timesheet/candidates`;

  return useQuery({
    queryKey: ['timesheetLinkCandidates', pid, toid],
    queryFn: () => timesheetRequest(authenticatedFetch, endpoint),
    enabled: Number.isInteger(pid) && (optionsEnabled ?? true),
    ...timesheetQueryOptions,
    staleTime: 0,
    ...restOptions,
  });
};

// mutate({ projectId, taskOrderId?, action, emsId? }): action 'create' adds a new EmployeeConnect
// project (or, with a taskOrderId, a child task), 'link' links the existing one emsId names, and
// 'unlink' removes the link.
export const useTimesheetLink = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId = null, action, emsId }) => {
      const pid = Number(projectId);
      const toid = normalizeTaskOrderId(taskOrderId);
      const base = toid ? `/projects/${pid}/task-orders/${toid}/timesheet` : `/projects/${pid}/timesheet`;
      if (action === 'create') return timesheetRequest(authenticatedFetch, base, 'POST', {});
      if (action === 'unlink') return timesheetRequest(authenticatedFetch, `${base}/link`, 'DELETE');
      const body = toid ? { emsTaskId: emsId } : { emsProjectId: emsId };
      return timesheetRequest(authenticatedFetch, `${base}/link`, 'PUT', body);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      await queryClient.invalidateQueries({ queryKey: ['projectTimesheet', pid] });
      await queryClient.invalidateQueries({ queryKey: ['timesheetLinkCandidates', pid] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

// --- PursuitConnect pursuit link (project page) ------------------------------------------------
//
// A project links to the PursuitConnect pursuit it came from. Both apps keep the link; ProPlan's
// server changes both sides. Errors carry the server's message (e.g. no access to the pursuit).

// { available, pursuit }: whether linking is set up on the server, and the linked pursuit
// ({ id, name, url }) or null.
export const useProjectPursuitLink = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const pid = Number(projectId);

  return useQuery({
    queryKey: ['projectPursuitLink', pid],
    queryFn: () => timesheetRequest(authenticatedFetch, `/projects/${pid}/pursuit-link`),
    enabled: Number.isInteger(pid) && (optionsEnabled ?? true),
    ...timesheetQueryOptions,
    ...restOptions,
  });
};

// Pursuits the signed-in person can see in PursuitConnect that no other project is linked to.
export const usePursuitLinkCandidates = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const pid = Number(projectId);

  return useQuery({
    queryKey: ['pursuitLinkCandidates', pid],
    queryFn: () => timesheetRequest(authenticatedFetch, `/projects/${pid}/pursuit-link/candidates`),
    enabled: Number.isInteger(pid) && (optionsEnabled ?? true),
    ...timesheetQueryOptions,
    staleTime: 0,
    ...restOptions,
  });
};

// mutate({ projectId, pursuitId }) links the project to that pursuit; mutate({ projectId, pursuitId: null })
// unlinks it.
export const useProjectPursuitLinkMutation = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, pursuitId }) => {
      const endpoint = `/projects/${Number(projectId)}/pursuit-link`;
      return pursuitId
        ? timesheetRequest(authenticatedFetch, endpoint, 'PUT', { pursuitId })
        : timesheetRequest(authenticatedFetch, endpoint, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      queryClient.setQueryData(['projectPursuitLink', pid], data);
      await queryClient.invalidateQueries({ queryKey: ['pursuitLinkCandidates', pid] });
      await queryClient.invalidateQueries({ queryKey: ['pursuitConnectPursuits'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};
